import { useState, useEffect, useRef } from 'react'
import { Save, Eye, EyeOff, Bell, Shield, Globe, Palette, Database, Mail, Hash, CheckCircle, XCircle, AtSign, FileText, AlertTriangle, Package, Send, Loader, Trash2, Plus, ShoppingBag, X, ChevronDown } from 'lucide-react'
import toast from 'react-hot-toast'
import { settingsService, restrictedCategoriesService, categoryService } from '../services/api'
import api from '../services/api'

const TAB_OPTIONS = ['General', 'Orders', 'Email', 'Security', 'Notifications', 'Slack', 'Appearance']

function Section({ title, children }) {
  return (
    <div className="admin-card space-y-4">
      <h3 className="font-display font-semibold text-white text-sm border-b border-surface-border pb-3">{title}</h3>
      {children}
    </div>
  )
}

function Field({ label, hint, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-3">
      <div className="sm:w-48 shrink-0">
        <label className="text-sm text-white font-medium">{label}</label>
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  )
}

function Toggle({ checked, onChange }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-5 rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-surface-border'}`}
    >
      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </button>
  )
}

function VatPreview({ vatEnabled, vatRate }) {
  const exclEx = 100
  const rate = parseFloat(vatRate) || 0
  const inclEx = vatEnabled ? exclEx * (1 + rate / 100) : exclEx
  return (
    <div className="bg-black/20 border border-surface-border rounded-xl p-4 mt-2">
      <p className="text-xs font-display font-semibold text-gray-500 uppercase tracking-wider mb-3">Price Preview</p>
      <div className="flex items-center gap-6 flex-wrap">
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-1">Excl. VAT</p>
          <p className="text-lg font-bold text-white">R {exclEx.toFixed(2)}</p>
        </div>
        {vatEnabled && (
          <>
            <div className="text-gray-600">→</div>
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">VAT ({rate}%)</p>
              <p className="text-lg font-bold text-yellow-400">+ R {(inclEx - exclEx).toFixed(2)}</p>
            </div>
            <div className="text-gray-600">→</div>
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Incl. VAT</p>
              <p className="text-xl font-bold text-primary">R {inclEx.toFixed(2)}</p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default function Settings() {
  const [activeTab, setActiveTab] = useState('General')
  const [showPassword, setShowPassword] = useState(false)
  const [saving, setSaving] = useState(false)

  const [general, setGeneral] = useState({
    storeName: "Tony's Liquor Distributor",
    email: 'admin@tonysliquor.co.za',
    phone: '+27 11 123 4567',
    address: '12 Trade Street, Johannesburg, 2000',
    currency: 'ZAR',
    timezone: 'Africa/Johannesburg',
  })

  const [tax, setTax] = useState({
    vatEnabled: true,
    vatRate: 15,
    pricesIncludeVat: true,
    showVatBreakdown: true,
    vatNumber: '',
    vatLabel: 'VAT',
  })

  const [notifications, setNotifications] = useState({
    newOrder: true,
    lowStock: true,
    newCustomer: false,
    dailyReport: true,
    emailAlerts: true,
    smsAlerts: false,
  })

  const [appearance, setAppearance] = useState({
    darkMode: true,
  })

  const [password, setPassword] = useState({ current: '', newPass: '', confirm: '' })

  // ── Inventory settings (wired to backend) ───────────────────────────────
  const [inventory, setInventory] = useState({
    lowStockThreshold: 10,
    loaded: false,
  })

  // ── Orders settings ──────────────────────────────────────────────────────
  const [orders, setOrders] = useState({
    showOrderStatus: false,
    loaded: false,
  })

  useEffect(() => {
    settingsService.get()
      .then(res => {
        const d = res?.data
        if (!d) return
        setInventory(inv => ({ ...inv, lowStockThreshold: d.low_stock_threshold ?? 10, loaded: true }))
        // Optionally seed other fields
        if (d.store_name) setGeneral(g => ({ ...g, storeName: d.store_name }))
        if (d.store_email) setGeneral(g => ({ ...g, email: d.store_email }))
        if (d.store_phone) setGeneral(g => ({ ...g, phone: d.store_phone }))
        if (d.vat_rate) setTax(t => ({ ...t, vatRate: parseFloat(d.vat_rate) }))
        if (d.vat_enabled !== undefined) setTax(t => ({ ...t, vatEnabled: d.vat_enabled }))
        // Orders visibility
        setOrders(o => ({ ...o, showOrderStatus: d.show_order_status ?? false, loaded: true }))
        // Seed Slack state from backend
        setSlack(s => ({
          ...s,
          enabled:             d.slack_enabled             ?? s.enabled,
          webhookUrl:          d.slack_webhook_url         ?? s.webhookUrl,
          channel:             d.slack_channel             ?? s.channel,
          botName:             d.slack_bot_name            ?? s.botName,
          notifyNewOrder:      d.slack_notify_new_order    ?? s.notifyNewOrder,
          notifyOrderStatus:   d.slack_notify_order_status ?? s.notifyOrderStatus,
          notifyLowStock:      d.slack_notify_low_stock    ?? s.notifyLowStock,
          notifyNewCustomer:   d.slack_notify_new_customer ?? s.notifyNewCustomer,
          notifyDailyReport:   d.slack_notify_daily_report ?? s.notifyDailyReport,
        }))
      })
      .catch(err => console.warn('[Settings] Could not load settings from API:', err?.response?.status, err?.message))
  }, [])

  const handleSavePassword = async () => {
    if (!password.current || !password.newPass) {
      toast.error('Enter your current and new password'); return
    }
    if (password.newPass.length < 8) {
      toast.error('New password must be at least 8 characters'); return
    }
    if (password.newPass !== password.confirm) {
      toast.error('New passwords do not match'); return
    }
    setSaving(true)
    try {
      await api.post('/me/change-password', {
        current_password: password.current,
        new_password: password.newPass,
      })
      toast.success('Password changed successfully')
      setPassword({ current: '', newPass: '', confirm: '' })
    } catch (e) {
      toast.error(e.message || 'Could not change password')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveInventory = async () => {
    setSaving(true)
    try {
      await settingsService.update({ low_stock_threshold: inventory.lowStockThreshold })
      toast.success('Inventory settings saved!')
    } catch {
      toast.error('Failed to save inventory settings')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveGeneral = async () => {
    setSaving(true)
    try {
      await settingsService.update({
        store_name: general.storeName,
        store_email: general.email,
        store_phone: general.phone,
        vat_rate: tax.vatRate,
        vat_enabled: tax.vatEnabled,
      })
      toast.success('Settings saved!')
    } catch {
      toast.error('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveOrders = async () => {
    setSaving(true)
    try {
      await settingsService.update({ show_order_status: orders.showOrderStatus })
      toast.success('Order settings saved!')
    } catch {
      toast.error('Failed to save order settings')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveSlack = async () => {
    setSaving(true)
    try {
      await settingsService.update({
        slack_enabled:             slack.enabled,
        slack_webhook_url:         slack.webhookUrl,
        slack_channel:             slack.channel,
        slack_bot_name:            slack.botName,
        slack_notify_new_order:    slack.notifyNewOrder,
        slack_notify_order_status: slack.notifyOrderStatus,
        slack_notify_low_stock:    slack.notifyLowStock,
        slack_notify_new_customer: slack.notifyNewCustomer,
        slack_notify_daily_report: slack.notifyDailyReport,
      })
      toast.success('Slack settings saved!')
    } catch {
      toast.error('Failed to save Slack settings')
    } finally {
      setSaving(false)
    }
  }

  const [emailSettings, setEmailSettings] = useState({
    orderNotifyAddress: 'orders@tonysliquor.co.za',
    ccAddresses: '',
    senderName: "Tony's Liquor Distributor",
    senderEmail: 'noreply@tonysliquor.co.za',
    attachCsv: true,
    activeTemplate: 'order',
  })

  const [slack, setSlack] = useState({
    enabled: false,
    webhookUrl: '',
    channel: '#orders',
    botName: "Tony's Bot",
    notifyNewOrder: true,
    notifyLowStock: true,
    notifyNewCustomer: false,
    notifyDailyReport: true,
    notifyOrderStatus: true,
  })
  const [testingSlack, setTestingSlack] = useState(false)
  const [slackTestResult, setSlackTestResult] = useState(null)

  const testSlackWebhook = async () => {
    if (!slack.webhookUrl.startsWith('https://hooks.slack.com/')) {
      toast.error('Please enter a valid Slack webhook URL')
      return
    }
    setTestingSlack(true)
    setSlackTestResult(null)
    try {
      const res = await fetch(slack.webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: slack.botName || "Tony's Bot",
          text: `✅ *Test notification from Tony's Liquor Admin*\nSlack notifications are configured and working!`,
          channel: slack.channel,
        }),
      })
      if (res.ok) {
        setSlackTestResult('success')
        toast.success('Test message sent to Slack!')
      } else {
        setSlackTestResult('error')
        toast.error('Webhook returned an error — check the URL')
      }
    } catch {
      setSlackTestResult('error')
      toast.error('Failed to reach Slack — check the webhook URL')
    } finally {
      setTestingSlack(false)
    }
  }

  const handleSave = async () => {
    if (activeTab === 'Slack') {
      await handleSaveSlack()
    } else if (activeTab === 'Orders') {
      await handleSaveOrders()
    } else {
      await handleSaveGeneral()
    }
  }

  // ── Test Email ────────────────────────────────────────────────────────────
  const [testEmailAddr, setTestEmailAddr]   = useState('')
  const [sendingTestEmail, setSendingTestEmail] = useState(false)
  const handleSendTestEmail = async () => {
    if (!testEmailAddr.trim()) return toast.error('Enter an email address')
    setSendingTestEmail(true)
    try {
      await api.post('/admin/test-email', { to_address: testEmailAddr.trim() })
      toast.success(`Test email sent to ${testEmailAddr}`)
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send test email')
    } finally {
      setSendingTestEmail(false)
    }
  }

  // ── 2FA TOTP ──────────────────────────────────────────────────────────────
  const [twoFaEnabled, setTwoFaEnabled]     = useState(false)
  const [twoFaLoading, setTwoFaLoading]     = useState(false)
  const [twoFaSetup, setTwoFaSetup]         = useState(null)   // { secret, qr_url }
  const [twoFaCode, setTwoFaCode]           = useState('')
  const [twoFaVerifying, setTwoFaVerifying] = useState(false)
  const [showDisableConfirm, setShowDisableConfirm] = useState(false)

  useEffect(() => {
    api.get('/admin/2fa/status').then(d => setTwoFaEnabled(d.enabled)).catch(() => {})
  }, [])

  const handle2FAToggle = async () => {
    if (twoFaEnabled) {
      setShowDisableConfirm(true)
    } else {
      setTwoFaLoading(true)
      try {
        const d = await api.post('/admin/2fa/setup')
        setTwoFaSetup(d)
        setTwoFaCode('')
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to start 2FA setup')
      } finally {
        setTwoFaLoading(false)
      }
    }
  }

  const handle2FAVerify = async () => {
    if (twoFaCode.length !== 6) return toast.error('Enter the 6-digit code from your authenticator app')
    setTwoFaVerifying(true)
    try {
      await api.post('/admin/2fa/verify', { code: twoFaCode })
      setTwoFaEnabled(true)
      setTwoFaSetup(null)
      setTwoFaCode('')
      toast.success('2FA enabled successfully!')
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invalid code — try again')
    } finally {
      setTwoFaVerifying(false)
    }
  }

  const handle2FADisable = async () => {
    try {
      await api.delete('/admin/2fa')
      setTwoFaEnabled(false)
      setShowDisableConfirm(false)
      toast.success('2FA disabled')
    } catch (e) {
      toast.error('Failed to disable 2FA')
    }
  }

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Tab nav */}
      <div className="flex gap-1 bg-black/30 border border-surface-border rounded-xl p-1">
        {TAB_OPTIONS.map(t => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-display font-semibold transition-all ${
              activeTab === t ? 'bg-primary text-white' : 'text-gray-500 hover:text-white'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* General */}
      {activeTab === 'General' && (
        <div className="space-y-4">
          <Section title="Store Information">
            <Field label="Store Name">
              <input className="input-field" value={general.storeName} onChange={e => setGeneral(g => ({ ...g, storeName: e.target.value }))} />
            </Field>
            <Field label="Contact Email">
              <input className="input-field" type="email" value={general.email} onChange={e => setGeneral(g => ({ ...g, email: e.target.value }))} />
            </Field>
            <Field label="Phone">
              <input className="input-field" value={general.phone} onChange={e => setGeneral(g => ({ ...g, phone: e.target.value }))} />
            </Field>
            <Field label="Address">
              <textarea className="input-field resize-none" rows={2} value={general.address} onChange={e => setGeneral(g => ({ ...g, address: e.target.value }))} />
            </Field>
          </Section>

          <Section title="Regional Settings">
            <Field label="Currency" hint="Used for all price displays">
              <select className="select-field" value={general.currency} onChange={e => setGeneral(g => ({ ...g, currency: e.target.value }))}>
                <option value="ZAR">ZAR — South African Rand</option>
                <option value="USD">USD — US Dollar</option>
                <option value="EUR">EUR — Euro</option>
                <option value="GBP">GBP — British Pound</option>
              </select>
            </Field>
            <Field label="Timezone">
              <select className="select-field" value={general.timezone} onChange={e => setGeneral(g => ({ ...g, timezone: e.target.value }))}>
                {/* ── Africa ───────────────────────────────────── */}
                <optgroup label="Africa">
                  <option value="Africa/Johannesburg">Africa/Johannesburg (UTC+2) — South Africa</option>
                  <option value="Africa/Cape_Verde">Africa/Cape_Verde (UTC−1)</option>
                  <option value="Africa/Abidjan">Africa/Abidjan (UTC+0) — Ghana, Ivory Coast</option>
                  <option value="Africa/Lagos">Africa/Lagos (UTC+1) — Nigeria, West Africa</option>
                  <option value="Africa/Nairobi">Africa/Nairobi (UTC+3) — Kenya, East Africa</option>
                  <option value="Africa/Cairo">Africa/Cairo (UTC+2) — Egypt</option>
                  <option value="Africa/Casablanca">Africa/Casablanca (UTC+1) — Morocco</option>
                </optgroup>
                {/* ── Europe ───────────────────────────────────── */}
                <optgroup label="Europe">
                  <option value="UTC">UTC (UTC+0)</option>
                  <option value="Europe/London">Europe/London (UTC+0/+1) — UK</option>
                  <option value="Europe/Lisbon">Europe/Lisbon (UTC+0/+1) — Portugal</option>
                  <option value="Europe/Paris">Europe/Paris (UTC+1/+2) — France, Germany, Spain</option>
                  <option value="Europe/Amsterdam">Europe/Amsterdam (UTC+1/+2) — Netherlands</option>
                  <option value="Europe/Rome">Europe/Rome (UTC+1/+2) — Italy</option>
                  <option value="Europe/Athens">Europe/Athens (UTC+2/+3) — Greece</option>
                  <option value="Europe/Helsinki">Europe/Helsinki (UTC+2/+3) — Finland</option>
                  <option value="Europe/Moscow">Europe/Moscow (UTC+3) — Russia</option>
                </optgroup>
                {/* ── Americas ─────────────────────────────────── */}
                <optgroup label="Americas">
                  <option value="America/Sao_Paulo">America/Sao_Paulo (UTC−3) — Brazil</option>
                  <option value="America/Argentina/Buenos_Aires">America/Argentina/Buenos_Aires (UTC−3)</option>
                  <option value="America/New_York">America/New_York (UTC−5/−4) — US East</option>
                  <option value="America/Chicago">America/Chicago (UTC−6/−5) — US Central</option>
                  <option value="America/Denver">America/Denver (UTC−7/−6) — US Mountain</option>
                  <option value="America/Los_Angeles">America/Los_Angeles (UTC−8/−7) — US West</option>
                  <option value="America/Toronto">America/Toronto (UTC−5/−4) — Canada East</option>
                  <option value="America/Vancouver">America/Vancouver (UTC−8/−7) — Canada West</option>
                  <option value="America/Mexico_City">America/Mexico_City (UTC−6/−5) — Mexico</option>
                </optgroup>
                {/* ── Asia & Middle East ───────────────────────── */}
                <optgroup label="Asia &amp; Middle East">
                  <option value="Asia/Dubai">Asia/Dubai (UTC+4) — UAE</option>
                  <option value="Asia/Riyadh">Asia/Riyadh (UTC+3) — Saudi Arabia</option>
                  <option value="Asia/Jerusalem">Asia/Jerusalem (UTC+2/+3) — Israel</option>
                  <option value="Asia/Kolkata">Asia/Kolkata (UTC+5:30) — India</option>
                  <option value="Asia/Dhaka">Asia/Dhaka (UTC+6) — Bangladesh</option>
                  <option value="Asia/Bangkok">Asia/Bangkok (UTC+7) — Thailand</option>
                  <option value="Asia/Singapore">Asia/Singapore (UTC+8) — Singapore</option>
                  <option value="Asia/Hong_Kong">Asia/Hong_Kong (UTC+8)</option>
                  <option value="Asia/Shanghai">Asia/Shanghai (UTC+8) — China</option>
                  <option value="Asia/Tokyo">Asia/Tokyo (UTC+9) — Japan</option>
                  <option value="Asia/Seoul">Asia/Seoul (UTC+9) — South Korea</option>
                </optgroup>
                {/* ── Pacific & Oceania ────────────────────────── */}
                <optgroup label="Pacific &amp; Oceania">
                  <option value="Australia/Perth">Australia/Perth (UTC+8)</option>
                  <option value="Australia/Adelaide">Australia/Adelaide (UTC+9:30/+10:30)</option>
                  <option value="Australia/Sydney">Australia/Sydney (UTC+10/+11)</option>
                  <option value="Pacific/Auckland">Pacific/Auckland (UTC+12/+13) — New Zealand</option>
                </optgroup>
              </select>
            </Field>
          </Section>

          <Section title="Tax & VAT">
            <Field label="Enable VAT" hint="Apply VAT to all product prices">
              <Toggle checked={tax.vatEnabled} onChange={v => setTax(t => ({ ...t, vatEnabled: v }))} />
            </Field>

            <Field label="VAT Rate (%)" hint="Current South African VAT rate is 15%">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  className="input-field w-24 text-sm"
                  value={tax.vatRate}
                  onChange={e => setTax(t => ({ ...t, vatRate: parseFloat(e.target.value) || 0 }))}
                  disabled={!tax.vatEnabled}
                />
                <span className="text-gray-400 text-sm">%</span>
                {tax.vatRate === 15 && (
                  <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full font-display font-medium">
                    ✓ SA Standard
                  </span>
                )}
              </div>
            </Field>

            <Field label="Price Display" hint="How prices are stored and shown">
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="vatMode"
                    checked={tax.pricesIncludeVat}
                    onChange={() => setTax(t => ({ ...t, pricesIncludeVat: true }))}
                    className="accent-primary"
                  />
                  <span className="text-sm text-gray-300">Prices include VAT <span className="text-gray-500">(incl. VAT)</span></span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="vatMode"
                    checked={!tax.pricesIncludeVat}
                    onChange={() => setTax(t => ({ ...t, pricesIncludeVat: false }))}
                    className="accent-primary"
                  />
                  <span className="text-sm text-gray-300">Prices exclude VAT <span className="text-gray-500">(VAT added at checkout)</span></span>
                </label>
              </div>
            </Field>

            <Field label="Show VAT Breakdown" hint="Display excl. and incl. prices on product pages">
              <Toggle checked={tax.showVatBreakdown} onChange={v => setTax(t => ({ ...t, showVatBreakdown: v }))} />
            </Field>

            <Field label="VAT Number" hint="Your business VAT registration number">
              <input
                className="input-field text-sm w-52"
                placeholder="e.g. 4123456789"
                value={tax.vatNumber}
                onChange={e => setTax(t => ({ ...t, vatNumber: e.target.value }))}
              />
            </Field>

            {/* Live VAT preview */}
            <VatPreview vatEnabled={tax.vatEnabled} vatRate={tax.vatRate} />
          </Section>
          <Section title="🏭 Inventory & Stock">
            <Field
              label="Low Stock Threshold"
              hint="Products with stock at or below this level trigger the low stock warning on the dashboard"
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="9999"
                    step="1"
                    className="input-field w-24 text-sm"
                    value={inventory.lowStockThreshold}
                    onChange={e => setInventory(i => ({ ...i, lowStockThreshold: Math.max(0, parseInt(e.target.value) || 0) }))}
                  />
                  <span className="text-gray-400 text-sm">units</span>
                </div>
                <button
                  onClick={handleSaveInventory}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary hover:bg-primary/80 text-white text-sm font-display font-semibold transition-colors disabled:opacity-50"
                >
                  <Save size={14} />
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
              <div className="mt-3 p-3 rounded-xl border border-surface-border bg-black/20 space-y-2">
                <p className="text-xs font-display font-semibold text-gray-500 uppercase tracking-wider">Preview</p>
                <div className="flex items-center gap-3 flex-wrap">
                  {[
                    { label: 'Critical', level: 1, color: 'text-red-400 bg-red-400/10 border-red-400/20' },
                    { label: 'Low', level: Math.round(inventory.lowStockThreshold * 0.5), color: 'text-amber-400 bg-amber-400/10 border-amber-400/20' },
                    { label: 'Threshold', level: inventory.lowStockThreshold, color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20' },
                    { label: 'Safe', level: inventory.lowStockThreshold + 5, color: 'text-green-400 bg-green-400/10 border-green-400/20' },
                  ].map(({ label, level, color }) => (
                    <div key={label} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-display font-semibold ${color}`}>
                      <Package size={12} />
                      {label}: {level} units
                      {level <= inventory.lowStockThreshold ? (
                        <AlertTriangle size={11} className="text-amber-400" />
                      ) : (
                        <CheckCircle size={11} className="text-green-400" />
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-600">
                  The dashboard will show a low stock alert for any product with ≤ {inventory.lowStockThreshold} units in stock.
                </p>
              </div>
            </Field>
          </Section>
        </div>
      )}
      {activeTab === 'Orders' && (
        <OrdersSettingsTab
          orders={orders}
          setOrders={setOrders}
          onSave={handleSaveOrders}
          saving={saving}
        />
      )}
      {activeTab === 'Email' && (
        <div className="space-y-4">
          <Section title="Order Notification Address">
            <Field label="Send Orders To" hint="New orders + CSV are emailed here">
              <div className="relative">
                <AtSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
                <input
                  className="input-field pl-9"
                  type="email"
                  placeholder="orders@tonysliquor.co.za"
                  value={emailSettings.orderNotifyAddress}
                  onChange={e => setEmailSettings(s => ({ ...s, orderNotifyAddress: e.target.value }))}
                />
              </div>
            </Field>
            <Field label="CC Addresses" hint="Comma-separated, e.g. manager@... , warehouse@...">
              <input
                className="input-field text-sm"
                type="text"
                placeholder="e.g. manager@tonysliquor.co.za, warehouse@tonysliquor.co.za"
                value={emailSettings.ccAddresses}
                onChange={e => setEmailSettings(s => ({ ...s, ccAddresses: e.target.value }))}
              />
            </Field>
            <Field label="Attach CSV" hint="Include order line items as CSV attachment">
              <Toggle checked={emailSettings.attachCsv} onChange={v => setEmailSettings(s => ({ ...s, attachCsv: v }))} />
            </Field>
          </Section>

          <Section title="Sender Details">
            <Field label="Sender Name" hint="Displayed as the 'From' name in emails">
              <input
                className="input-field"
                value={emailSettings.senderName}
                onChange={e => setEmailSettings(s => ({ ...s, senderName: e.target.value }))}
              />
            </Field>
            <Field label="Sender Email" hint="Reply-to address for outgoing emails">
              <input
                className="input-field"
                type="email"
                value={emailSettings.senderEmail}
                onChange={e => setEmailSettings(s => ({ ...s, senderEmail: e.target.value }))}
              />
            </Field>
          </Section>

          <Section title="Email Templates">
            <div className="flex items-start gap-3 bg-primary/10 border border-primary/20 rounded-xl p-4 mb-4">
              <span className="text-xl mt-0.5">ℹ️</span>
              <div>
                <p className="text-sm font-medium text-white mb-1">Fully branded emails are generated automatically</p>
                <p className="text-xs text-gray-400 leading-relaxed">
                  Both the <strong className="text-gray-300">Customer Confirmation</strong> and <strong className="text-gray-300">Order Notification</strong> emails
                  are built from the branded template you see in the preview below — complete with the Tony's Liquor logo,
                  teal/gold header, itemised order table, and footer contact details.
                  No manual template editing is required.
                </p>
              </div>
            </div>

            {/* Template switcher — still drives the preview */}
            <div className="flex gap-1 bg-black/30 border border-surface-border rounded-lg p-1 w-fit">
              {[['order', '📋 Order Notification'], ['customer', '👤 Customer Confirmation']].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setEmailSettings(s => ({ ...s, activeTemplate: val }))}
                  className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    emailSettings.activeTemplate === val ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </Section>

          {/* Preview */}
          <Section title="Email Preview">
            <p className="text-xs text-gray-500 -mt-1 mb-3">
              Exact preview of the branded email your customers and your team will receive.
            </p>
            {(() => {
              const sampleItems = [
                { name: 'Johnnie Walker Black Label 750ml', qty: 2, unit: 'R 459.00', total: 'R 918.00', sc: 'WH001' },
                { name: 'Nederburg Cabernet Sauvignon 750ml', qty: 6, unit: 'R 149.00', total: 'R 894.00', sc: 'WN002' },
                { name: 'Heineken 24 x 330ml Case', qty: 1, unit: 'R 339.00', total: 'R 339.00', sc: 'BH003' },
              ]
              const itemRows = sampleItems.map((i, idx) => `
                <tr style="background:${idx % 2 === 0 ? '#fff' : '#fafafa'}">
                  <td style="padding:10px 12px;color:#999;font-size:12px;font-family:monospace;border-bottom:1px solid #f0f0f0">${i.sc}</td>
                  <td style="padding:10px 12px;color:#333;font-size:13px;border-bottom:1px solid #f0f0f0">${i.name}</td>
                  <td style="padding:10px 12px;color:#555;font-size:13px;text-align:center;border-bottom:1px solid #f0f0f0">${i.qty}</td>
                  <td style="padding:10px 12px;color:#555;font-size:13px;text-align:right;border-bottom:1px solid #f0f0f0">${i.unit}</td>
                  <td style="padding:10px 12px;color:#0e947a;font-size:13px;font-weight:600;text-align:right;border-bottom:1px solid #f0f0f0">${i.total}</td>
                </tr>`).join('')

              // Logo URL — mirrors backend SITE_URL logic
              const logoUrl = `${window.location.protocol}//${window.location.hostname}:8090/logo.text-w.webp`
              const isOrder = emailSettings.activeTemplate === 'order'
              const headline = isOrder ? 'New Order Received 🛒' : 'Order #1042 Confirmed ✓'
              const subline  = isOrder
                ? 'A new order has been placed and requires your attention.'
                : 'Your order has been received and is being processed.'

              // Customer body — matches backend customer_body_inner
              const customerBodyInner = `
                <p style="color:#333;font-size:15px;margin:0 0 20px">
                  Hi <strong>John Smith</strong>, thank you for your order!<br>
                  We've received it and will be in touch to confirm your delivery.
                </p>
                <table width="100%" cellpadding="0" cellspacing="0"
                       style="background:#f9f9f9;border-radius:8px;padding:16px 20px;margin-bottom:24px;border:1px solid #eee">
                  <tr>
                    <td style="padding:6px 0;color:#888;font-size:13px;width:130px">Order number</td>
                    <td style="padding:6px 0;color:#0e947a;font-size:15px;font-weight:700">#1042</td>
                  </tr>
                  <tr>
                    <td style="padding:6px 0;color:#888;font-size:13px">Deliver to</td>
                    <td style="padding:6px 0;color:#333;font-size:13px">12 Trade Street, Pretoria, 0001</td>
                  </tr>
                </table>
                <table width="100%" cellpadding="0" cellspacing="0" style="border-radius:8px;overflow:hidden;border:1px solid #eee;margin-bottom:20px">
                  <thead>
                    <tr style="background:#0e947a">
                      <th style="padding:10px 12px;color:#fff;font-size:12px;text-align:left;text-transform:uppercase;letter-spacing:.5px">Product</th>
                      <th style="padding:10px 12px;color:#fff;font-size:12px;text-align:center;text-transform:uppercase">Qty</th>
                      <th style="padding:10px 12px;color:#fff;font-size:12px;text-align:right;text-transform:uppercase">Unit</th>
                      <th style="padding:10px 12px;color:#fff;font-size:12px;text-align:right;text-transform:uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${sampleItems.map((i, idx) => `
                    <tr style="background:${idx % 2 === 0 ? '#fff' : '#fafafa'}">
                      <td style="padding:10px 12px;color:#333;font-size:13px;border-bottom:1px solid #f0f0f0">${i.name}</td>
                      <td style="padding:10px 12px;color:#555;font-size:13px;text-align:center;border-bottom:1px solid #f0f0f0">${i.qty}</td>
                      <td style="padding:10px 12px;color:#555;font-size:13px;text-align:right;border-bottom:1px solid #f0f0f0">${i.unit}</td>
                      <td style="padding:10px 12px;color:#0e947a;font-size:13px;font-weight:600;text-align:right;border-bottom:1px solid #f0f0f0">${i.total}</td>
                    </tr>`).join('')}
                  </tbody>
                </table>
                <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:20px">
                  <tr>
                    <td style="text-align:right;padding:12px 0">
                      <span style="font-size:13px;color:#888">Order Total (incl. VAT)&nbsp;&nbsp;</span>
                      <span style="font-size:22px;font-weight:800;color:#0e947a">R 2,151.00</span>
                    </td>
                  </tr>
                </table>
                <p style="color:#888;font-size:13px;margin:0">
                  Questions? Call us on <a href="tel:+27123231520" style="color:#0e947a">012 323 1520</a>
                  or email <a href="mailto:orders@tonysliquor.co.za" style="color:#0e947a">orders@tonysliquor.co.za</a>.
                </p>`

              // Order (client) body — matches backend client_body_inner
              const orderBodyInner = `
                <table width="100%" cellpadding="0" cellspacing="0"
                       style="background:#f9f9f9;border-radius:8px;padding:16px 20px;margin-bottom:24px;border:1px solid #eee">
                  <tr>
                    <td style="padding:6px 0;color:#888;font-size:13px;width:130px">Order number</td>
                    <td style="padding:6px 0;color:#0e947a;font-size:15px;font-weight:700">#1042</td>
                  </tr>
                  <tr>
                    <td style="padding:6px 0;color:#888;font-size:13px">Customer</td>
                    <td style="padding:6px 0;color:#333;font-size:13px">John Smith</td>
                  </tr>
                  <tr>
                    <td style="padding:6px 0;color:#888;font-size:13px">Deliver to</td>
                    <td style="padding:6px 0;color:#333;font-size:13px">12 Trade Street, Pretoria, 0001</td>
                  </tr>
                </table>
                <table width="100%" cellpadding="0" cellspacing="0" style="border-radius:8px;overflow:hidden;border:1px solid #eee;margin-bottom:20px">
                  <thead>
                    <tr style="background:#0e947a">
                      <th style="padding:10px 12px;color:#fff;font-size:12px;text-align:left;text-transform:uppercase;letter-spacing:.5px">Code</th>
                      <th style="padding:10px 12px;color:#fff;font-size:12px;text-align:left;text-transform:uppercase">Product</th>
                      <th style="padding:10px 12px;color:#fff;font-size:12px;text-align:center;text-transform:uppercase">Qty</th>
                      <th style="padding:10px 12px;color:#fff;font-size:12px;text-align:right;text-transform:uppercase">Unit</th>
                      <th style="padding:10px 12px;color:#fff;font-size:12px;text-align:right;text-transform:uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody>${itemRows}</tbody>
                </table>
                <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:20px">
                  <tr>
                    <td style="text-align:right;padding:12px 0">
                      <span style="font-size:13px;color:#888">Order Total (incl. VAT)&nbsp;&nbsp;</span>
                      <span style="font-size:22px;font-weight:800;color:#0e947a">R 2,151.00</span>
                    </td>
                  </tr>
                </table>`

              const bodyInner = isOrder ? orderBodyInner : customerBodyInner

              // Exact replica of backend email_wrapper
              const brandedHtml = `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f5f5f5;font-family:'Segoe UI',Arial,sans-serif">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f5f5f5;padding:30px 0">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%">
        <tr>
          <td style="background:#0e0e0e;padding:24px 36px;border-radius:12px 12px 0 0;text-align:center">
            <img src="${logoUrl}" alt="Tony's Liquor Distributor"
                 style="height:48px;width:auto;object-fit:contain;display:block;margin:0 auto"
                 onerror="this.style.display='none'" />
            <div style="font-size:11px;color:#666;margin-top:10px;letter-spacing:2px;text-transform:uppercase">
              Order Notification
            </div>
          </td>
        </tr>
        <tr><td style="background:linear-gradient(90deg,#0e947a,#d4af37,#0e947a);height:3px"></td></tr>
        <tr>
          <td style="background:#111;padding:24px 36px;text-align:center">
            <div style="font-size:28px;font-weight:700;color:#d4af37">${headline}</div>
            <div style="font-size:14px;color:#aaa;margin-top:6px">${subline}</div>
          </td>
        </tr>
        <tr>
          <td style="background:#ffffff;padding:28px 36px;border-radius:0 0 12px 12px">
            ${bodyInner}
            <div style="margin-top:28px;padding-top:20px;border-top:1px solid #f0f0f0;text-align:center;color:#aaa;font-size:12px">
              Tony's Liquor Distributor &nbsp;|&nbsp; 012 323 1520 &nbsp;|&nbsp; orders@tonysliquor.co.za
              <br><span style="font-size:10px;color:#ccc">This is an automated message — please do not reply directly to this email.</span>
            </div>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body></html>`

              const previewHtml = brandedHtml
              const tabLabel = isOrder ? 'Order Notification' : 'Customer Confirmation'

              return (
                <div className="border border-surface-border rounded-xl overflow-hidden">
                  <div className="bg-black/20 px-3 py-2 border-b border-surface-border flex items-center gap-2 text-xs text-gray-500">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400/60"/>
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/60"/>
                    <span className="w-2.5 h-2.5 rounded-full bg-green-400/60"/>
                    <span className="ml-2">{tabLabel} — sample data preview</span>
                  </div>
                  <iframe
                    srcDoc={previewHtml}
                    className="w-full border-0"
                    style={{ height: '600px' }}
                    title="Email preview"
                    sandbox="allow-same-origin"
                  />
                </div>
              )
            })()}
          </Section>

          {/* Send Test Email */}
          <Section title="Send Test Email">
            <p className="text-xs text-gray-500 -mt-1 mb-3">
              Send a sample order notification to verify your email configuration.
            </p>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1.5 font-medium">Recipient Address</label>
                <input
                  type="email"
                  value={testEmailAddr}
                  onChange={e => setTestEmailAddr(e.target.value)}
                  placeholder="your@email.com"
                  className="input-field w-full text-sm"
                />
              </div>
              <button
                onClick={handleSendTestEmail}
                disabled={sendingTestEmail || !testEmailAddr.trim()}
                className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50 shrink-0"
              >
                {sendingTestEmail
                  ? <><Loader size={14} className="animate-spin"/> Sending…</>
                  : <><Send size={14}/> Send Test</>}
              </button>
            </div>
          </Section>
        </div>
      )}

      {/* Security */}
      {activeTab === 'Security' && (
        <div className="space-y-4">
          <Section title="Change Password">
            <Field label="Current Password">
              <div className="relative">
                <input
                  className="input-field pr-10"
                  type={showPassword ? 'text' : 'password'}
                  value={password.current}
                  onChange={e => setPassword(p => ({ ...p, current: e.target.value }))}
                  placeholder="Enter current password"
                />
                <button
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </Field>
            <Field label="New Password">
              <div className="relative">
                <input
                  className="input-field pr-10"
                  type={showPassword ? 'text' : 'password'}
                  value={password.newPass}
                  onChange={e => setPassword(p => ({ ...p, newPass: e.target.value }))}
                  placeholder="Min. 8 characters"
                />
                <button
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </Field>
            <Field label="Confirm Password">
              <div className="relative">
                <input
                  className="input-field pr-10"
                  type={showPassword ? 'text' : 'password'}
                  value={password.confirm}
                  onChange={e => setPassword(p => ({ ...p, confirm: e.target.value }))}
                  placeholder="Repeat new password"
                />
                <button
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </Field>
            <button
              onClick={handleSavePassword}
              disabled={saving}
              className="admin-btn-solid mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 hover:opacity-90"
            >
              <Save size={14} /> {saving ? 'Saving…' : 'Update Password'}
            </button>
          </Section>

          <Section title="Session & Access">
            <Field label="Two-Factor Auth" hint="Add an extra layer of security">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Toggle
                    checked={twoFaEnabled}
                    onChange={handle2FAToggle}
                  />
                  {twoFaLoading
                    ? <span className="text-xs text-gray-400 flex items-center gap-1"><Loader size={12} className="animate-spin"/> Loading…</span>
                    : twoFaEnabled
                      ? <span className="text-xs font-medium text-green-400 flex items-center gap-1"><CheckCircle size={12}/> Active</span>
                      : <span className="text-xs text-gray-500">Not configured</span>}
                </div>

                {/* Setup modal (shown after clicking toggle to enable) */}
                {twoFaSetup && !twoFaEnabled && (
                  <div className="bg-black/30 border border-surface-border rounded-xl p-4 space-y-4">
                    <p className="text-xs text-gray-300 font-medium">Scan this QR code with Google Authenticator, Authy, or any TOTP app:</p>
                    <div className="flex justify-center">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(twoFaSetup.qr_url)}&size=180x180&bgcolor=ffffff`}
                        alt="2FA QR Code"
                        className="rounded-lg border-4 border-white"
                        width={180}
                        height={180}
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Or enter this secret manually:</p>
                      <code className="text-xs bg-black/40 px-3 py-1.5 rounded-lg text-primary font-mono tracking-widest select-all">{twoFaSetup.secret}</code>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5 font-medium">Enter 6-digit verification code</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={twoFaCode}
                          onChange={e => setTwoFaCode(e.target.value.replace(/\D/g, ''))}
                          placeholder="000000"
                          className="input-field w-36 text-center text-lg font-mono tracking-widest"
                        />
                        <button
                          onClick={handle2FAVerify}
                          disabled={twoFaVerifying || twoFaCode.length !== 6}
                          className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50"
                        >
                          {twoFaVerifying ? <><Loader size={13} className="animate-spin"/> Verifying…</> : <><CheckCircle size={13}/> Verify & Enable</>}
                        </button>
                        <button onClick={() => setTwoFaSetup(null)} className="btn-secondary text-sm">Cancel</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Disable confirm */}
                {showDisableConfirm && (
                  <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4 space-y-3">
                    <p className="text-sm text-red-300 font-medium">Disable Two-Factor Authentication?</p>
                    <p className="text-xs text-gray-400">This will remove the extra security layer from your account.</p>
                    <div className="flex gap-2">
                      <button onClick={handle2FADisable} className="bg-red-600 hover:bg-red-500 text-white text-sm px-4 py-1.5 rounded-lg transition-colors">Yes, disable 2FA</button>
                      <button onClick={() => setShowDisableConfirm(false)} className="btn-secondary text-sm">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </Field>
            <Field label="Session Timeout" hint="Auto logout after inactivity">
              <select className="select-field w-40">
                <option>30 minutes</option>
                <option>1 hour</option>
                <option>4 hours</option>
                <option>Never</option>
              </select>
            </Field>
          </Section>
        </div>
      )}

      {/* Notifications */}
      {activeTab === 'Notifications' && (
        <div className="space-y-4">
          <Section title="Email Notifications">
            {[
              { key: 'newOrder', label: 'New Order', hint: 'When a customer places an order' },
              { key: 'lowStock', label: 'Low Stock Alert', hint: 'When product stock falls below threshold' },
              { key: 'newCustomer', label: 'New Customer', hint: 'When a new account is registered' },
              { key: 'dailyReport', label: 'Daily Summary', hint: 'Daily sales and activity report' },
            ].map(item => (
              <Field key={item.key} label={item.label} hint={item.hint}>
                <Toggle
                  checked={notifications[item.key]}
                  onChange={v => setNotifications(n => ({ ...n, [item.key]: v }))}
                />
              </Field>
            ))}
          </Section>

          <Section title="Delivery Channels">
            <Field label="Email Alerts">
              <Toggle checked={notifications.emailAlerts} onChange={v => setNotifications(n => ({ ...n, emailAlerts: v }))} />
            </Field>
            <Field label="SMS Alerts" hint="Requires Twilio configuration">
              <Toggle checked={notifications.smsAlerts} onChange={v => setNotifications(n => ({ ...n, smsAlerts: v }))} />
            </Field>
          </Section>
        </div>
      )}

      {/* Slack */}
      {activeTab === 'Slack' && (
        <div className="space-y-4">
          <Section title="Slack Integration">
            <Field label="Enable Slack" hint="Send notifications to a Slack channel">
              <Toggle checked={slack.enabled} onChange={v => setSlack(s => ({ ...s, enabled: v }))} />
            </Field>
            <Field label="Webhook URL" hint={
              <span>
                Get from{' '}
                <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noopener noreferrer"
                  className="text-primary hover:underline">api.slack.com/messaging/webhooks</a>
              </span>
            }>
              <div className="flex gap-2">
                <input
                  className="input-field text-sm flex-1"
                  placeholder="https://hooks.slack.com/services/T.../B.../..."
                  value={slack.webhookUrl}
                  onChange={e => setSlack(s => ({ ...s, webhookUrl: e.target.value }))}
                  disabled={!slack.enabled}
                />
                <button
                  onClick={testSlackWebhook}
                  disabled={!slack.enabled || !slack.webhookUrl || testingSlack}
                  className="btn px-4 bg-[#4A154B] hover:bg-[#611f69] text-white border-0 disabled:opacity-40 text-sm flex items-center gap-2 flex-shrink-0"
                >
                  {testingSlack ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : slackTestResult === 'success' ? (
                    <CheckCircle size={14} className="text-green-300" />
                  ) : slackTestResult === 'error' ? (
                    <XCircle size={14} className="text-red-300" />
                  ) : (
                    <Hash size={14} />
                  )}
                  {testingSlack ? 'Sending…' : 'Test'}
                </button>
              </div>
              {slackTestResult === 'success' && (
                <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                  <CheckCircle size={11} /> Test message delivered successfully
                </p>
              )}
              {slackTestResult === 'error' && (
                <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                  <XCircle size={11} /> Delivery failed — verify the webhook URL
                </p>
              )}
            </Field>
            <Field label="Channel" hint="Channel or DM to post to (e.g. #orders)">
              <input
                className="input-field text-sm w-48"
                placeholder="#orders"
                value={slack.channel}
                onChange={e => setSlack(s => ({ ...s, channel: e.target.value }))}
                disabled={!slack.enabled}
              />
            </Field>
            <Field label="Bot Display Name" hint="Name shown in Slack">
              <input
                className="input-field text-sm w-48"
                placeholder="Tony's Bot"
                value={slack.botName}
                onChange={e => setSlack(s => ({ ...s, botName: e.target.value }))}
                disabled={!slack.enabled}
              />
            </Field>
          </Section>

          <Section title="Notify on Events">
            {[
              { key: 'notifyNewOrder',    label: 'New Order',             hint: 'Post when a customer places an order' },
              { key: 'notifyOrderStatus', label: 'Order Status Change',   hint: 'Post when an order status is updated' },
              { key: 'notifyLowStock',    label: 'Low Stock Alert',       hint: 'Post when stock falls below threshold' },
              { key: 'notifyNewCustomer', label: 'New Customer',          hint: 'Post when a new account is registered' },
              { key: 'notifyDailyReport', label: 'Daily Summary',         hint: 'Morning summary of previous day' },
            ].map(item => (
              <Field key={item.key} label={item.label} hint={item.hint}>
                <Toggle
                  checked={slack[item.key]}
                  onChange={v => setSlack(s => ({ ...s, [item.key]: v }))}
                />
              </Field>
            ))}
          </Section>

          {/* Message preview */}
          <Section title="Message Preview">
            <div className="bg-[#1a1a2e] rounded-xl p-4 border border-[#4A154B]/40">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#4A154B] flex items-center justify-center text-lg flex-shrink-0">🥃</div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white text-sm font-bold">{slack.botName || "Tony's Bot"}</span>
                    <span className="text-[10px] text-gray-500 bg-gray-700 px-1.5 py-0.5 rounded">APP</span>
                    <span className="text-xs text-gray-500">Today at 9:41 AM</span>
                  </div>
                  <div className="bg-[#222] rounded-lg p-3 border-l-4 border-[#0e947a]">
                    <p className="text-white text-sm font-semibold">🛒 New Order #1042</p>
                    <p className="text-gray-400 text-xs mt-1">Customer: <span className="text-gray-200">John Smith</span></p>
                    <p className="text-gray-400 text-xs">Total: <span className="text-green-400 font-medium">R 1,249.00</span></p>
                    <p className="text-gray-400 text-xs">Status: <span className="text-yellow-400">Pending</span></p>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              This is a preview of how order notifications will appear in Slack.
            </p>
          </Section>

          {/* Slack save button */}
          <div className="flex justify-end pt-2">
            <button onClick={handleSaveSlack} className="btn-primary" disabled={saving}>
              {saving ? (
                <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
              ) : (
                <><Save size={14} /> Save Slack Settings</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Appearance */}
      {activeTab === 'Appearance' && (
        <Section title="Interface Preferences">
          <Field label="Dark Mode" hint="Admin panel always uses dark mode">
            <Toggle checked={appearance.darkMode} onChange={v => setAppearance(a => ({ ...a, darkMode: v }))} />
          </Field>
        </Section>
      )}

      {/* Save button */}
      <div className="flex justify-end pt-2">
        <button onClick={handleSave} className="btn-primary" disabled={saving}>
          {saving ? (
            <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
          ) : (
            <><Save size={14} /> Save Changes</>
          )}
        </button>
      </div>
    </div>
  )
}

// ── Orders Settings Tab ───────────────────────────────────────────────────────
function OrdersSettingsTab({ orders, setOrders, onSave, saving }) {
  const [restricted, setRestricted]           = useState([])
  const [allCategories, setAllCategories]     = useState([])
  const [loadingRestricted, setLoadingRestricted] = useState(true)
  const [addingCat, setAddingCat]             = useState(false)
  const [catSearch, setCatSearch]             = useState('')
  const [dropdownOpen, setDropdownOpen]       = useState(false)
  const [selectedCat, setSelectedCat]         = useState(null)  // pending selection, not yet added
  const dropdownRef                           = useRef(null)

  // Load restricted list and full category list in parallel
  useEffect(() => {
    Promise.all([
      restrictedCategoriesService.getAll(),
      categoryService.getAll(),
    ])
      .then(([restricted, cats]) => {
        setRestricted(Array.isArray(restricted) ? restricted : [])
        // Sort alphabetically, deduplicate by name
        const seen = new Set()
        const sorted = (Array.isArray(cats) ? cats : [])
          .filter(c => { if (seen.has(c.name.toLowerCase())) return false; seen.add(c.name.toLowerCase()); return true })
          .sort((a, b) => a.name.localeCompare(b.name))
        setAllCategories(sorted)
      })
      .catch(() => toast.error('Could not load categories'))
      .finally(() => setLoadingRestricted(false))
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false)
        setCatSearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Categories available to add = all categories minus those already restricted
  const restrictedNames = new Set(restricted.map(r => r.name.toLowerCase()))
  const available = allCategories.filter(c =>
    !restrictedNames.has(c.name.toLowerCase()) &&
    c.name.toLowerCase().includes(catSearch.toLowerCase())
  )

  const handleAddCat = async () => {
    if (!selectedCat) return
    setAddingCat(true)
    setDropdownOpen(false)
    try {
      const res = await restrictedCategoriesService.add(selectedCat)
      setRestricted(prev => [...prev, res])
      setSelectedCat(null)
      setCatSearch('')
      toast.success(`"${selectedCat}" added to restricted list`)
    } catch (e) {
      const status = e?.response?.status
      if (status === 409) {
        toast.error(`"${selectedCat}" is already in the restricted list`)
      } else {
        toast.error('Failed to add category — please try again')
      }
    } finally {
      setAddingCat(false)
    }
  }

  const handleRemoveCat = async (id, name) => {
    try {
      await restrictedCategoriesService.remove(id)
      setRestricted(prev => prev.filter(r => r.id !== id))
      toast.success(`"${name}" removed`)
    } catch {
      toast.error('Failed to remove category')
    }
  }

  return (
    <div className="space-y-6">
      {/* Order Status Visibility */}
      <Section title="Order Status Visibility">
        <Field
          label="Show order status to customers"
          hint="When OFF, customers and the PWA will not see order status badges or tracking information. Orders still process normally — this only controls visibility. Default: OFF."
        >
          <div className="flex items-center gap-4">
            <Toggle
              checked={orders.showOrderStatus}
              onChange={v => setOrders(o => ({ ...o, showOrderStatus: v }))}
            />
            <span className={`text-sm font-medium ${orders.showOrderStatus ? 'text-green-400' : 'text-gray-500'}`}>
              {orders.showOrderStatus ? 'Status visible to customers' : 'Status hidden from customers'}
            </span>
          </div>
          {orders.showOrderStatus && (
            <div className="mt-3 flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
              <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-300">
                Order status will be shown on customer order lists and the mobile app.
                Only enable this when your business has a reliable delivery tracking workflow.
              </p>
            </div>
          )}
        </Field>
        <div className="flex justify-end pt-2">
          <button onClick={onSave} className="btn-primary flex items-center gap-2" disabled={saving}>
            {saving
              ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
              : <><Save size={14} /> Save Order Settings</>
            }
          </button>
        </div>
      </Section>

      {/* Single-Unit Restricted Categories */}
      <Section title="Single-Unit Restricted Categories">
        <Field
          label="Categories that cannot be sold as a single unit"
          hint="Products in these categories will only appear in packs of 2 or more (e.g. 6-pack beer, case of water). Categories NOT listed here (e.g. Wine, Spirits, Whisky, Keg) can be purchased as a single unit."
        >
          {loadingRestricted ? (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
              <div className="w-3 h-3 border border-gray-500 border-t-transparent rounded-full animate-spin" />
              Loading…
            </div>
          ) : (
            <div className="space-y-3">

              {/* Add from real category dropdown — placed first so dropdown opens into available space */}
              <div className="space-y-2" ref={dropdownRef}>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { setDropdownOpen(v => !v); setCatSearch('') }}
                    disabled={addingCat || available.length === 0}
                    className="w-full flex items-center justify-between gap-2 input-field text-sm text-left disabled:opacity-50"
                  >
                    <span className={selectedCat ? 'text-white' : available.length === 0 ? 'text-gray-600 italic' : 'text-gray-500'}>
                      {addingCat
                        ? 'Adding…'
                        : selectedCat
                          ? selectedCat
                          : available.length === 0
                            ? 'All categories already restricted'
                            : 'Select a category to restrict…'}
                    </span>
                    <ChevronDown size={14} className={`text-gray-400 flex-shrink-0 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {dropdownOpen && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-surface-card border border-surface-border rounded-xl shadow-xl overflow-hidden">
                      <div className="p-2 border-b border-surface-border">
                        <input
                          type="text"
                          value={catSearch}
                          onChange={e => setCatSearch(e.target.value)}
                          placeholder="Search categories…"
                          autoFocus
                          className="w-full bg-surface-elevated border border-surface-border rounded-lg px-3 py-1.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-primary"
                        />
                      </div>
                      <div className="max-h-56 overflow-y-auto">
                        {available.length === 0 ? (
                          <div className="px-4 py-3 text-xs text-gray-500 italic">
                            No matching categories
                          </div>
                        ) : (
                          available.map(cat => (
                            <button
                              key={cat.id}
                              type="button"
                              onClick={() => {
                                setSelectedCat(cat.name)
                                setDropdownOpen(false)
                                setCatSearch('')
                              }}
                              className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2
                                ${selectedCat === cat.name
                                  ? 'bg-primary/20 text-primary'
                                  : 'text-gray-300 hover:bg-primary/10 hover:text-primary'}`}
                            >
                              <Plus size={12} className="text-gray-500 flex-shrink-0" />
                              {cat.name}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Add button — only enabled when a category is selected */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleAddCat}
                    disabled={!selectedCat || addingCat}
                    className="btn-primary flex items-center gap-1.5 px-4 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {addingCat
                      ? <><span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Adding…</>
                      : <><Plus size={13} /> Add to restricted list</>
                    }
                  </button>
                  {selectedCat && (
                    <button
                      onClick={() => setSelectedCat(null)}
                      className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      Clear
                    </button>
                  )}
                </div>

                <p className="text-xs text-gray-600 pl-1">
                  Only categories from your actual product catalogue are shown. Already-restricted categories are hidden.
                </p>
              </div>

              {/* Current list with count badge */}
              <div className="rounded-xl border border-surface-border bg-surface-elevated/40 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Current restricted categories
                  </span>
                  <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full font-semibold">
                    {restricted.length} {restricted.length === 1 ? 'entry' : 'entries'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 min-h-[2rem]">
                  {restricted.length === 0 && (
                    <span className="text-xs text-gray-600 italic">No restricted categories configured — all categories allow single units.</span>
                  )}
                  {restricted.map(r => (
                    <span
                      key={r.id}
                      className="inline-flex items-center gap-1.5 bg-surface-border/40 border border-surface-border rounded-full px-3 py-1 text-xs text-gray-300"
                    >
                      <ShoppingBag size={10} className="text-primary" />
                      {r.name}
                      <button
                        onClick={() => handleRemoveCat(r.id, r.name)}
                        className="ml-1 text-gray-500 hover:text-red-400 transition-colors"
                        title={`Remove "${r.name}"`}
                      >
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <p className="text-[10px] text-gray-600">
                Tip: matches are partial — "Beer" will match "Beers", "Beer (Can)", "Craft Beer", etc.
              </p>
            </div>
          )}
        </Field>
      </Section>
    </div>
  )
}
